import { AssetFile } from 'contentful';
import { Container } from '../../../../components/Container/Container';
import { Heading } from '../../../../components/Heading/Heading';
import { ProPaymentPlans } from '../../../../components/ProPaymentPlans/ProPaymentPlans';
import { Text } from '../../../../components/Text/Text';
import { ProPlanId } from '@domain/premium/plans';
import { trackEvent } from '@domain/analytics/trackEvent';
import { useClientHasRendered } from '@domain/session/useClientHasRendered';
import { useIsTabletScreenWidthOrWider } from '@hooks/useScreenWidth';
import { ICoupon } from '@app-types/stripe';
import { CMSChoosePaymentPlanPreviewImage } from './CMSChoosePaymentPlanPreviewImage';
import { CMSChoosePaymentPlanSectionLoader } from './CMSChoosePaymentPlanSectionLoader';

interface IProps {
  couponIsLoading: boolean;
  coupon?: ICoupon;
  userHasBeenPro: boolean;
  mainTitle: string;
  offerTitle?: string;
  planCardTitle?: string;
  phonePreviewDesktop?: AssetFile;
  phonePreviewTablet?: AssetFile;
  phonePreviewMobile?: AssetFile;
  currentPage: string;
  position: number;
  proPlanId: ProPlanId;
  onPlanSelect: (proPlanId: ProPlanId) => void;
  submitLink?: string;
  onSubmitLinkClick?: () => void;
}

export const CMSChoosePaymentPlanSection = ({
  userHasBeenPro,
  couponIsLoading,
  coupon,
  mainTitle,
  offerTitle,
  planCardTitle,
  phonePreviewDesktop,
  phonePreviewTablet,
  phonePreviewMobile,
  currentPage,
  position,
  proPlanId,
  onPlanSelect,
  submitLink,
  onSubmitLinkClick,
}: IProps) => {
  const isTabletScreenWidthOrWider = useIsTabletScreenWidthOrWider();
  const clientHasRendered = useClientHasRendered();

  return (
    <Container
      className="pb-sm flex flex-col items-center pt-8 sm:flex-row sm:justify-center sm:gap-8"
      variant="thin"
      data-testid="CMSChoosePaymentPlanSection"
    >
      {!clientHasRendered ? (
        <CMSChoosePaymentPlanSectionLoader
          hasOfferTitle={!!offerTitle}
          hasPreviewImage={!!phonePreviewDesktop}
        ></CMSChoosePaymentPlanSectionLoader>
      ) : (
        <>
          {!isTabletScreenWidthOrWider && (
            <>
              <>
                {offerTitle && (
                  <Text
                    variant="lg"
                    className="text-clownfish-shade mb-4 text-center font-semibold sm:mb-0 sm:text-left md:text-left"
                  >
                    {offerTitle}
                  </Text>
                )}
              </>

              <Heading className="mt-0 text-center" variant="md" as="h1">
                {mainTitle}
              </Heading>
            </>
          )}
          {phonePreviewDesktop && (
            <div className="relative flex w-full items-center justify-end sm:w-1/2">
              <CMSChoosePaymentPlanPreviewImage
                phonePreviewDesktop={phonePreviewDesktop}
                phonePreviewMobile={phonePreviewMobile}
                phonePreviewTablet={phonePreviewTablet}
              />
            </div>
          )}
          <div className={'flex w-full flex-col items-center sm:w-1/2'}>
            <div
              className={`flex w-full flex-col ${phonePreviewDesktop ? 'sm:max-w-max' : 'items-center'}`}
            >
              {isTabletScreenWidthOrWider && (
                <span>
                  {offerTitle && (
                    <Text
                      variant="lg"
                      className="text-clownfish-shade mb-4 text-center font-semibold sm:mb-0 sm:text-left md:text-left"
                    >
                      {offerTitle}
                    </Text>
                  )}
                  <Heading
                    className="mt-0 text-center sm:text-left md:max-w-lg md:text-left"
                    variant="md"
                    as="h1"
                  >
                    {mainTitle}
                  </Heading>
                </span>
              )}
              <ProPaymentPlans
                className="col-start-1 row-start-2 px-6"
                coupon={coupon}
                couponIsLoading={couponIsLoading}
                backgroundColour="bg-sea-foam"
                submitLink={submitLink}
                proPlanId={proPlanId}
                onPlanSelect={onPlanSelect}
                userHasBeenPro={userHasBeenPro}
                planCardTitle={planCardTitle}
                onSubmitLinkClick={() => {
                  onSubmitLinkClick?.();
                  trackEvent({
                    name: 'mykiss_cta_button_clicked',
                    properties: {
                      type: 'pro',
                      source: 'choose-payment-plan-section',
                      position,
                      page: currentPage,
                    },
                  });
                }}
              />
            </div>
          </div>
        </>
      )}
    </Container>
  );
};
